import { constants, isLogEventFailure } from '@trqk/utils';

import { getLogEventErrorMessage } from '.';

const { INGESTION_PROGRESS_CHUNKS } = constants;

/**
 * Get the next task events for a statement upload, based upon the contents of a log event.
 * @param {LogEvent} event the log event
 * @param {ManageStatementsTask} task a task for uploading and ingesting the statement
 * @returns {TaskEvent[]}
 */
export const getUploadStatementTaskEvents = (event: LogEvent): TaskEvent[] => {
    const matchId: MatchId<FilenameInput, UploadStatementOutput> = (
        task: Task<FilenameInput, UploadStatementOutput>,
    ): boolean => {
        const taskStatementId =
            task?.output?.statementView?.statement_id ?? '0';
        const eventStatementId = event?.detail?.statement_id ?? '1';

        return taskStatementId === eventStatementId;
    };

    const statusMessage =
        event?.detail?.status_message ?? 'unknown ingestion status';

    let capitalizedStatusMessage = statusMessage;

    if (statusMessage.length > 0) {
        const firstChar = statusMessage[0].toUpperCase();
        const afterFirstChar = statusMessage.slice(1);

        capitalizedStatusMessage = `${firstChar}${afterFirstChar}`;
    }

    const status = event?.eventStatus ?? 'none';

    if (isLogEventFailure(event)) {
        return [
            {
                type: 'ERROR',
                value: {
                    matchId,
                    error: getLogEventErrorMessage(event),
                    output: {
                        qrrOverage: event?.detail?.qrr_overage,
                    },
                },
            },
        ];
    }

    if (status === 'success') {
        const statement_id = event?.detail?.statement_id ?? null;

        return [
            // Include a PROGRESS task so that the progress bar is displayed at 100% full before it
            // disappears
            {
                type: 'PROGRESS',
                value: {
                    matchId,
                    progress: 100,
                    output: {
                        statusMessage: 'Ingestion complete',
                    },
                },
            },
            {
                type: 'SUCCESS',
                value: {
                    matchId,
                    output: {
                        statement_id,
                    },
                },
            },
        ];
    }

    if (
        Object.prototype.hasOwnProperty.call(INGESTION_PROGRESS_CHUNKS, status)
    ) {
        const progress = INGESTION_PROGRESS_CHUNKS?.[status] ?? null;

        if (progress !== null) {
            return [
                {
                    type: 'PROGRESS',
                    value: {
                        matchId,
                        progress,
                        output: {
                            statusMessage: capitalizedStatusMessage,
                        },
                    },
                },
            ];
        }
    }

    return [];
};
